/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ResourceAdminState } from '@app/app/page-modules/resource/store/admin/resource-admin.state';
import { Form, ProjectFormContent } from '@app/app/shared/models';
import { Select } from '@ngxs/store';
import { Observable, takeUntil } from 'rxjs';

@Component({
  selector: 'ptl-form-project',
  templateUrl: './form-project.component.html',
  styleUrls: ['../form.component.scss', './form-project.component.scss'],
})
export class FormProjectComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  private subscriptionEnd$ = new EventEmitter<void>();

  @Input() form?: Form;
  @Input() canRemoveItem?: boolean;
  @Input() position?: number | string;

  @Output() formElementAdded = new EventEmitter<Form>();
  @Output() formElementRemoved = new EventEmitter<void>();
  @Output() formElementUpdated = new EventEmitter<Form<ProjectFormContent>>();

  @ViewChild('formTitleInput') private formTitleInput: ElementRef<HTMLInputElement>;

  @Select(ResourceAdminState.contentStateChanged)
  contentStateChanged$: Observable<boolean>;

  checkboxForm: FormGroup;
  expanded = true;
  hasError = false;
  focused = false;
  saveInProgress = false;
  forceExpand = false;

  constructor(private fb: FormBuilder) {
    this.checkboxForm = this.fb.group({
      title: ['', [Validators.required]],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.form?.content) {
      this.setFormContent(this.form.content as ProjectFormContent, this.form.newAddedForm);
    }
  }

  ngOnInit(): void {
    if (this.form && !!this.form.uid) {
      this.setFormContent(this.form.content as ProjectFormContent, this.form.newAddedForm);
    }

    this.contentStateChanged$.pipe(takeUntil(this.subscriptionEnd$)).subscribe(() => {
      this.saveInProgress = false;
    });

    this.forceExpand = this.form?.content?.forceExpand;
  }

  ngAfterViewInit(): void {
    const titleInput = this.formTitleInput?.nativeElement;
    if (this.forceExpand && titleInput) {
      setTimeout(() => {
        const titleLength = titleInput.value.length;
        titleInput.focus();
        titleInput.setSelectionRange(titleLength, titleLength);
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptionEnd$?.emit();
  }

  saveForm(): void {
    this.hasError = false;
    if (this.checkboxForm.valid) {
      this.emitFormUpdate();
      if (!this.form?.uid) {
        this.outputData();
      } else {
        this.formElementAdded.emit(this.form);
      }
      this.expanded = false;
      this.forceExpand = false;
      this.saveInProgress = false;
    } else {
      this.hasError = true;
    }
  }

  expandForm(): void {
    this.expanded = true;
    this.forceExpand = false;
  }

  collapseForm(event: PointerEvent): void {
    event.stopPropagation();
    this.expanded = false;
    this.forceExpand = false;
  }

  outputData(): void {
    if (this.form && this.checkboxForm) {
      const outputData: Form = {
        ...this.form,
        newAddedForm: false,
        content: {
          ...this.checkboxForm.value,
          type: 'PROJECT',
        } as ProjectFormContent,
      };

      this.formElementAdded.emit(outputData);
    }
  }

  removeForm(): void {
    this.formElementRemoved.emit();
  }

  private setFormContent(content: ProjectFormContent, newAddedForm?: boolean): void {
    if (!content) {
      return;
    }

    this.expanded = newAddedForm ?? this.expanded;
    this.checkboxForm.patchValue(
      {
        title: content.title,
      },
      { emitEvent: false },
    );
  }

  private emitFormUpdate(): void {
    if (!this.form || !this.checkboxForm) {
      return;
    }

    const existingContent = (this.form.content as ProjectFormContent) ?? ({} as ProjectFormContent);
    const value = this.checkboxForm.getRawValue();
    const content: ProjectFormContent = {
      ...existingContent,
      ...value,
      type: 'PROJECT',
      forceExpand: false,
      submitted: existingContent?.submitted ?? false,
      formIsActive: existingContent?.formIsActive ?? false,
    };
    const payload: Form<ProjectFormContent> = {
      ...(this.form as Form<ProjectFormContent>),
      newAddedForm: this.form?.uid ? false : (this.form?.newAddedForm ?? false),
      content,
    };

    this.form = payload as Form;
    this.formElementUpdated.emit(payload);
  }
}
